// Phase 12 — shared Documents section for CaseView and CollectiveCaseView.
//
// Replaces the two near-duplicate inline csSec-gendocs blocks that lived in
// each view. Owns: section header (with GenerateDocButton + add-link +
// lock), folder card (Open/Create/Link/Relink/Unlink), links list, the
// add/edit-link modal, and the optional pagination state. The folder card
// and link rows use the agenda-event row shell (.ai) so they read the same
// as the agenda pane.
//
// Per-view differences (kept intentionally — see Phase 12 spec):
//   - Section title key: caller passes `titleKey` (case.documents vs
//     cc.section.general_documents).
//   - Persistence engine: caller passes `onUpdateCase(patch)`, which is
//     CaseView's global sync (s(SK.ca,…)) or CollectiveCaseView's local
//     updCs helper. Both accept a partial-entity patch.
//   - Delete-link UX: caller passes `onDeleteLink(lk)`. CaseView wraps it
//     in confirmAndUndo; CollectiveCaseView just deletes + toasts.
//   - Pagination: CaseView passes paginate=true; CollectiveCaseView does
//     not. Pagination state is local to this component.

// react/react-in-jsx-scope — every JSX element here counted it (the house remedy, AttachmentSaveModal.tsx; 211 §5)
import React, { useState, useMemo, lazy, Suspense } from "react";
import {
  HardDrive,
  Cloud,
  Folder,
  Globe,
  FileText,
  Link2,
  Database,
  Archive,
  ExternalLink as ExtLink2,
  BookOpen,
  Paperclip,
  Server,
  Pencil,
  Trash2,
  X,
  Lock,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { useTranslation } from "../i18n";
import { usePermissions } from "../hooks/usePermissions";
import type { Translate } from "../lib/interestRefusal"; // 211 §5
import {
  SectionHeader,
  AddButton,
  LockButton,
  ModalLayout,
  InfoHint,
  ListNotLoaded,
} from "./SharedUI";
import { ListLoadContext, PAGE_LISTS } from "../lib/listLoad"; // 211 §10 — the templates, when they could not load
import { usePageList, usePageListContext, readList } from "../hooks/usePageList";
import { GenerateDocButton } from "./GenerateDocModal";
import { openOneDrivePicker } from "../lib/loadOnUse.mjs"; // 208 §1 — the picker loads the first time it opens
import { openClientFolder } from "../lib/clientFolder";
import CaseLibraryCopySection from "./CaseLibraryCopySection";
import {
  api, // 174 §1.2 — the folder calls go through the one door, which sends BOTH credentials
  // 140 THE RECONCILE — the offer fires finalize's own steps 3+4, measured
  // re-runnable, rather than carrying a second copy of them.
  convertCaseFinalize,
  convertCollectiveCaseFinalize,
} from "../api";
// 140 THE RECONCILE — the folder-name rule, one home for browser and server.
import {
  folderRefIsStale,
  folderNameOnDisk,
  folderLastSegment,
} from "../lib/folderName.mjs";
import { contrastText, nid, fd, coIni } from "../utils";
import { CT, RAD, FS } from "../lib/tokens";
import MenuSelect from "./MenuSelect"; // 154B §2
import { useConfirm } from "./useConfirmAndUndo";
import { orderIdsByParty } from "../lib/memberOrder"; // 216 §4 [RULED 71, 73] — a collective case's client folders by last name
import { usePager, pageOf } from "../hooks/usePager"; // 186 B.7a
import Pager from "./Pager";

// Phase 21 — document billing relocated out of the Emails toolbar into the
// Documents section. Lazy so the docx/pdf billing UI is only pulled into the
// bundle when a user opens it.
const DocumentBillingModal = lazy(() => import("./DocumentBillingModal"));

// Cluster sub-pane card: three coherent .dc cards stacked in the
// Documents area. Each card carries its own title INSIDE.
// 196 §B.1 [RULED, Laurent 2026-09-19] — EXPORTED, NOT COPIED: the two
// Communication panes (CaseEmailsSection, WhatsappImportPane) and the messages
// drawer's own heading wear THIS dress — « DOSSIER DE LA CAUSE »'s — through
// these two names, so one screen's spacing is every screen's and no pane
// invents a fourth.
export const PANE_TITLE = {
  fontSize: FS.s11,
  margin: "0 0 8px",
  fontWeight: 700,
  color: "var(--ts)",
  textTransform: "uppercase",
  letterSpacing: ".4px",
};
export const PANE_BODY = { display: "flex", flexDirection: "column", gap: 8 } as const;

// 140 THE RECONCILE — what `POST /:id/convert-finalize` answers, both kinds.
// `did` is the arm it took; everything else depends on which arm that was.
type FinalizeResult = {
  ok?: boolean;
  ref?: string;
  folder?: {
    did?: "renamed" | "created" | "already_named" | "rename_failed" | "create_failed";
    path?: string;
    webUrl?: string | null;
    code?: string;
    error?: string | null;
    restamped?: Record<string, number>;
  };
};

const ICON_MAP = {
  HardDrive,
  Cloud,
  Folder,
  Globe,
  FileText,
  Link: Link2,
  Database,
  Archive,
  ExternalLink: ExtLink2,
  BookOpen,
  Paperclip,
  Server,
};

export default function CaseDocumentsSection({
  kind, // 'case' | 'cc'
  cs, // case or collective-case entity
  cl,
  op, // for GenerateDocButton parties picker
  co, // for currentUser initials
  currentUser,
  linkTypesList = [],
  stageTypesList = [],
  toast,
  docLocked,
  setDocLocked,
  canViewDocs,
  canManageDocs,
  canLinkDocs,
  isAdmin,
  isRPHere,
  assigned,
  titleKey, // i18n key for header title
  paginate = false, // CV true, CCV false
  pageSize = 10,
  /* (partial) => void — patches folder/links onto cs.
     130 §3(a) — AND IT ALSO RETURNS THE ENGINE'S OUTCOME. Both call sites
     (`CaseView.tsx:2354`, `CollectiveCaseView.tsx:5233`) are concise arrows
     over `s(SK.ca|SK.cc, …)`, and `persist` returns
     `Promise<{ok, failed, total}>` (`App.tsx:4769`) — so the promise was
     already coming back and was already being dropped. Documented rather than
     added: the union of what the call sites pass, not a subset. */
  onUpdateCase,
  onDeleteLink, // (lk) => void — view-supplied delete handler
  // Phase 13 Item 4 — caller drives section collapse. When `collapsible` is
  // true the SectionHeader gets a left chevron and the body sub-panes only
  // render when `sectionOpen` is truthy. Default open=true so callers that
  // don't opt in keep the previous always-visible behavior.
  collapsible = false,
  sectionOpen = true,
  onSectionToggle,
  // 140 THE RECONCILE — only `caseFolderNameTemplate` is read, and only so the
  // offer can NAME the folder it is proposing. Both views already hold this.
  firmSettings,
  /* 176 §3.2 — App's `openProvisionModal`, threaded the way `firmSettings` and
     `toast` already are: App → CaseView / CCV → here. ONE prop, and §3.2 rules
     that a prop is the price to pay rather than build a second provisioning
     path. MEASURED before it was threaded: the modal it opens renders in App's
     TOP-LEVEL JSX (sibling of `linksMgr` and `orphanDlg`, outside the `content`
     div), so it is not section-gated and opening it from a matter works.

     176 §3.3 — `(ids: number[]) => void`, and the ids are the POINT. §3.2 called
     it with nothing, which opened the FIRM-WIDE list: every folderless client's
     name, e-mail and linked matter, shown to whoever clicked a repair inside one
     matter. Laurent found it on staging. The scope is now passed and the modal
     narrows to it. Do not call this with no argument from here — that is the
     clients-list behaviour and it is the leak. */
  onProvisionFolders,
}: any) {
  const { t } = useTranslation();
  const askConfirm = useConfirm();
  const { can, hidden } = usePermissions();
  const [linkMod, setLinkMod] = useState<any>(null);
  // 186 B.7a [RULED 186 (10)] — the pager is a shared mechanism now (the
  // `page` state, the arithmetic and the `.csPgr` control moved to
  // hooks/usePager.ts + components/Pager.tsx); `pageSize` is this caller's own.
  const pager = usePager();
  // Phase 41 (D4) — per-client "Client file" subsection collapse. Collapsed by
  // default on CollectiveCaseView (kind==='cc'); expanded on CaseView. Driven by
  // the caller's `kind`, not a fork — one shared component.
  const [clientFilesOpen, setClientFilesOpen] = useState(kind !== "cc");
  // Phase 21 — document billing relocated here from the Emails toolbar.
  // AUTH stage: gate now follows the stand-alone section key
  // caseView.documents.billing (role default or per-case grant), replacing the
  // retired per-user emailBillingEnabled flag.
  const [docBillOpen, setDocBillOpen] = useState(false);

  // Templates for the inline "Generate procedural documents" sub-pane.
  // We fetch on mount (cheap call; same list the modal used to fetch on
  // open). Surface as a <select> the user picks BEFORE clicking Generate.
  const [tplPick, setTplPick] = useState("");
  const scopeForGen = kind === "cc" ? { ccId: cs.id } : { caseId: cs.id };
  const canGenerate =
    !!assigned || can("caseView.documents.generate", scopeForGen);
  // Phase 21 AUTH — document-billing sub-pane gate (stand-alone section key).
  const canBillDocs = can("caseView.documents.billing", scopeForGen);
  /* 211 §5 — « Renommer » [ADVISOR: gated by caseView.documents.edit — « Upload,
     edit, rename, replace documents », server/src/lib/sectionRegistry.js]: the
     billing window's own file list, each ticked file renamed by its date. */
  const canRenameDocs = can("caseView.documents.edit", scopeForGen);
  const [docRenameOpen, setDocRenameOpen] = useState(false);
  const tr = t as Translate; // 211 §5 — typed at the seam for the pane's words
  /* 211 §10 — asked the 210 §1 way (src/hooks/usePageList.ts): it answered
     `[]` when it failed, and the pane said « No templates with an uploaded
     file. Add one in Settings » of templates that were there. */
  const _tpl = usePageList<any>(PAGE_LISTS.docTemplates, "document templates", canGenerate ? "/doc-templates" : null, readList);
  const _tplLists = usePageListContext(_tpl.entry);
  // P17.9 — the 'interest-memo' template is system-managed (auto-selected
  // by the calculator's memo generator), not a case-document the user picks.
  const templates = useMemo(
    () => _tpl.rows.filter((x) => x.languages && x.languages.length && x.kind !== "interest-memo"),
    [_tpl.rows],
  );

  const cLinks = cs.links || [];

  const getLinkIcon = (typeId: any) => {
    const lt = linkTypesList.find((x: any) => x.id === typeId);
    return lt ? (ICON_MAP as any)[lt.icon] || Link2 : Link2;
  };
  const getLinkColor = (typeId: any) => {
    const lt = linkTypesList.find((x: any) => x.id === typeId);
    return lt ? lt.iconColor : "var(--tm)";
  };
  const getLinkTag = (typeId: any) => {
    const lt = linkTypesList.find((x: any) => x.id === typeId);
    const nc = lt?.nameColor || lt?.iconColor || CT.c_8892a4;
    return { name: lt?.name || "Other", bg: nc, text: contrastText(nc) };
  };

  const isAdminLink = canManageDocs;
  const curIni = currentUser
    ? coIni(co.find((x: any) => x.id === currentUser))
    : "";

  const saveLink = (lk2: any) => {
    if (!lk2.name || !lk2.url) {
      toast(
        t("docs.link.name_url_required", {
          fallback: "⚠ Name and URL required",
        }),
      );
      return;
    }
    const nl = {
      id: lk2._id || nid(cLinks.length ? cLinks : [{ id: 0 }]),
      name: lk2.name,
      url: lk2.url,
      type: lk2.type || "other",
      dateAdded: lk2._id
        ? cLinks.find((x: any) => x.id === lk2._id)?.dateAdded ||
          new Date().toISOString().slice(0, 10)
        : new Date().toISOString().slice(0, 10),
      addedBy: lk2._id
        ? cLinks.find((x: any) => x.id === lk2._id)?.addedBy || curIni
        : curIni,
    };
    if (lk2._id)
      onUpdateCase({ links: cLinks.map((x: any) => (x.id === lk2._id ? nl : x)) });
    else onUpdateCase({ links: [...cLinks, nl] });
    setLinkMod(null);
    toast(
      lk2._id
        ? t("toast.updated", { fallback: "Updated" })
        : t("toast.added", { fallback: "Added" }),
    );
  };

  // Folder card handlers.
  const folderQueryParam = kind === "cc" ? "&ccId=" : "&caseId=";
  const updRoot = (p: any) => onUpdateCase({ caseRootFolder: p });

  const _doOpen = async () => {
    try {
      // 174 §1.2 — THE BUTTON THE DEFECT WAS REPORTED ON. Through api(), which
      // sends BOTH credentials (the lx:token Bearer and the lx_session
      // cookie); the bare fetch sent the cookie alone and 401'd for anyone
      // holding only the token. api() supplies the /api prefix.
      const j = (await api.get(
        "/auth/microsoft/browse?path=" +
          encodeURIComponent(cs.caseRootFolder) +
          folderQueryParam +
          cs.id +
          "&mode=resolve",
      )) as { currentFolder?: { webUrl?: string } };
      if (j.currentFolder?.webUrl) {
        window.open(j.currentFolder.webUrl, "_blank");
        return;
      }
      /* 176 §1.2(d) — a toast, so the sentence is APPENDED, not hung on an
         icon. It is here because a folder that will not open is exactly when
         a reader suspects the rename they just did; it did not cause this. */
      toast(
        "⚠ " +
          t("toast.couldnt_open_folder", { fallback: "Couldn't open folder" }) +
          " " +
          t("folder.permanence_hint", {
            fallback: "The link points at the folder, not at its name.",
          }),
      );
    } catch (e2) {
      toast(
        "⚠ " +
          ((e2 as any).message || t("toast.open_failed", { fallback: "Open failed" })),
      );
    }
  };

  const _create = async () => {
    try {
      // 174 §1.2 — through api(): BOTH credentials, plus the X-LexCRM a
      // cookie-auth mutation needs. api() supplies the /api prefix.
      const j = (await api.post("/auth/microsoft/create-case-folder", {
        ref: cs.ref,
        title: cs.title,
      })) as { path?: string; existed?: boolean; name?: string };
      updRoot(j.path);
      toast(
        j.existed
          ? t("toast.folder.linked_to_existing", {
              name: j.name || "",
              fallback: "Linked to existing folder: " + (j.name || ""),
            })
          : t("toast.folder.created", {
              name: j.name || "",
              fallback: "Created new folder: " + (j.name || ""),
            }),
      );
    } catch (e2) {
      toast(
        "⚠ " +
          ((e2 as any).message ||
            t("toast.create_failed", { fallback: "Create failed" })),
      );
    }
  };

  /* ── BRIEF 130 §3 — THE FOLDER-LINK BUTTON: HONEST NOW, CORRECT ON PROD ────
   *
   * THREE DEFECTS IN FIVE LINES, and they had to be fixed together.
   *
   * (i) `picked.isFolder` was a key `openOneDrivePicker` had NEVER carried, so
   *     this whole branch never ran: the button silently did nothing. 129 §3
   *     measured that and deliberately did not add the key, because of (ii).
   *
   * (ii) `picked.raw?.parentReference?.path` is likewise never present —
   *     `/browse`'s item mapping sends `{id,name,isFolder,webUrl,size,
   *     lastModified,childCount}` (`microsoft.js:403-411`) and its
   *     current-folder fallback sends four of those (`:414-420`). So
   *     `parentPath` was ALWAYS `""` and the write was `"/" + name` — every
   *     ancestor dropped.
   *
   * (iii) and that path never reached the database anyway:
   *     `folderValidation.js:20-28` refuses anything outside the configured
   *     library with a 400 `case.folder.outside_library`. The fence held.
   *
   * §3(b) — THE CORRECT PATH, and it is `browse`'s own, never
   * `parentReference`. The picker now carries `libraryPath`: the browsed
   * folder's path in the coordinate space `caseRootFolder` is stored in.
   * `path` alone would NOT do — it is in-drive, because the server's
   * `resolveDriveAndPath` consumes the library segment, and
   * `createCaseFolder` states the opposite requirement for the stored column
   * in its own comment (`m365.js:348-350`). Nothing is subtracted by hand: the
   * server answers both forms and the client picks the one its column takes.
   *
   * §3(a) — THE HONEST REFUSAL. `updRoot` goes through the `s()` engine, which
   * PUTs asynchronously, rolls the row back on a refusal and toasts the
   * server's own translated sentence (`App.tsx:4462` already knows
   * `case.folder.outside_library` and `case.folder.already_claimed`). What was
   * wrong is the ORDER: "Linked: <name>" fired synchronously, before the
   * server had answered, so a refused link would have said success and then
   * contradicted itself. The engine's outcome is awaited and success is
   * claimed only when it says so. A refused link now says one thing.
   */
  const _linkOrRelink = async () => {
    try {
      // Phase 23 — folder-setup picker: browses the firm library (mode=link,
      // server-bounded to the configured startPath, never bare drive root).
      const picked = await openOneDrivePicker({
        mode: "link",
        ...(kind === "cc" ? { ccId: cs?.id } : { caseId: cs?.id }),
      });
      if (!picked) return; // cancelled — the user just closed it
      if (!picked.isFolder) {
        toast(
          t("case.folder.pick_a_folder", {
            fallback: "Pick a folder, not a file — this is the matter's own folder.",
          }),
        );
        return;
      }
      /* `null` means the server could not place this folder in the library's
         own space, and the one thing this must not do is guess. Writing
         `picked.path` here would be refused as outside the library, and
         writing `"/" + name` is the defect this § exists for. */
      if (!picked.libraryPath) {
        toast(
          t("case.folder.place_unknown", {
            fallback:
              "That folder could not be placed inside the firm's library, so nothing was linked.",
          }),
        );
        return;
      }
      const outcome = await updRoot(picked.libraryPath);
      /* An engine call with nothing to do resolves `{ok:true, total:0}`, and a
         refusal resolves `{ok:false}` having already said WHY. Only `ok` earns
         the success sentence; a `void` return (a caller that does not answer)
         is read as ok, which is what every other consumer of this prop does. */
      if (outcome && outcome.ok === false) return;
      toast(t("toast.linked", { fallback: "Linked" }) + ": " + picked.name);
    } catch (e2) {
      toast(
        "⚠ " +
          ((e2 as any).message ||
            t("toast.picker_failed", { fallback: "Picker failed" })),
      );
    }
  };

  const _unlink = async () => {
    askConfirm({
      // 161C §3
      title: t("confirm.unlink_folder_title", { fallback: "Unlink the folder?" }),
      message: t("case.folder.unlink_confirm", {
        fallback:
          "Unlink the folder from this case? The folder itself will not be deleted.",
      }),
      onConfirm: () => {
        updRoot(null);
        toast(t("case.folder.unlinked_toast", { fallback: "Folder unlinked." }));
      },
    });
  };

  // Pagination slicing.
  const { cur: __cur, tot: __tot, pageItems: __pageItems } = pageOf(cLinks, pager.page, pageSize);
  const __pg = paginate ? __pageItems : cLinks;

  const fn = cs.caseRootFolder
    ? cs.caseRootFolder.split("/").filter(Boolean).pop() || cs.caseRootFolder
    : null;

  /* ── BRIEF 140, THE RECONCILE [RULED, Laurent, 2026-08-27] ─────────────────
   *
   * §2's finalize is fired by the CLIENT when the undo window expires. Closing
   * the tab inside that window skips it: the matter converts correctly and its
   * folder keeps the prospect name, with nothing that will ever fix it. This
   * closes that — BY OFFERING, never automatically. Never on load, never a
   * warning colour, never a nag: a row that says what is true, and a button.
   *
   * THE CONDITION IS DELIBERATELY NARROW (`folderRefIsStale`): a formerRef
   * exists AND this folder's own last segment still contains it, spelled the
   * way a folder name spells it — `sanitizeFolderName` turns "/" into "-", so
   * the raw ref would never match and the offer would silently never appear.
   * It is NOT "the name differs from what the template would produce": that
   * fires on any matter whose title was ever edited, which is most of them,
   * and a folder the firm renamed on purpose is not wrong.
   *
   * `renameOffer` is the name the offer PROMISES, built through the same two
   * shared functions the server pipeline applies, so the sentence cannot
   * promise something the rename will not deliver. */
  const _staleFolderRef = folderRefIsStale(cs.caseRootFolder, cs.formerRef)
    ? String(cs.formerRef)
    : null;
  const _renameTo = _staleFolderRef
    ? folderNameOnDisk(firmSettings?.caseFolderNameTemplate, {
        ref: cs.ref,
        title: cs.title,
      })
    : null;
  const [reconciling, setReconciling] = useState(false);
  const _reconcileFolder = async () => {
    if (reconciling) return;
    setReconciling(true);
    try {
      // Finalize's steps 3+4, not a second copy of them: measured safely
      // re-runnable before this call site was written (see the route's note).
      const r = (kind === "cc"
        ? await convertCollectiveCaseFinalize(cs.id)
        : await convertCaseFinalize(cs.id)) as FinalizeResult | null;
      const did = r?.folder?.did;
      if (did === "renamed" && r?.folder?.path) {
        const path = r.folder.path;
        const named = path.split("/").filter(Boolean).pop() || "";
        updRoot(path);
        toast(
          t("case.folder.reconciled", {
            name: named,
            fallback: "Folder renamed: " + named,
          }),
        );
      } else if (did === "already_named") {
        // Not a failure: the folder already carries the name. Nothing to say
        // beyond that, and the row disappears on the next read either way.
        toast(
          t("case.convert.folder_kept", {
            fallback: "casefile folder already up to date",
          }),
        );
      } else {
        toast(
          "⚠ " +
            (r?.folder?.error ||
              t("case.convert.folder_skipped", {
                fallback: "casefile folder not updated",
              })),
        );
      }
    } catch (e2) {
      toast("⚠ " + ((e2 as Error).message || ""));
    } finally {
      setReconciling(false);
    }
  };

  // Phase 41 (D4) — per-linked-client "Client file" rows. HARD GATE (fail-
  // closed): GET /api/clients returns clientRootFolder UNGATED, so we render
  // NOTHING unless the viewer passes the client-folder gate. On this surface
  // that gate is the case-scoped RIGHT caseView.clientFile.view (the SAME right
  // the server enforces via isClientFileDelegate): admin OR RP-of-this-matter OR
  // a live per-collaborator grant on this matter. Grant-aware — a delegated
  // collaborator actually SEES the link (no proforma-style shown/enforced drift).
  // One labelled row per linked client. 175 §3.3 — a client WITH a folder opens
  // through the shared resolve-and-open path; a client WITHOUT one says so
  // instead of vanishing, because vanishing is indistinguishable from having no
  // right to see the pane at all.
  const _clientName = (c: any) => {
    if (!c) return "";
    if ((c.type || "").toLowerCase() === "company")
      return (
        c.companyName ||
        [c.firstName, c.lastName].filter(Boolean).join(" ") ||
        `#${c.id}`
      );
    return (
      [c.firstName, c.lastName].filter(Boolean).join(" ") ||
      c.companyName ||
      `#${c.id}`
    );
  };
  const _clientFileCtx = kind === "cc" ? { ccId: cs.id } : { caseId: cs.id };
  // P20f — hidden qualifies the admin/RP short-circuits, mirroring the server
  // (isClientFileDelegate consults the same plane before its own admin/RP arms).
  const _showClientFiles =
    !hidden("caseView.clientFile.view") &&
    (!!isAdmin || !!isRPHere || can("caseView.clientFile.view", _clientFileCtx));
  const _linkedClientIds =
    kind === "cc"
      ? [
          ...new Set(
            (cs.clientLinks || [])
              .map((l: any) =>
                typeof l === "number"
                  ? l
                  : Number(l && (l.clId ?? l.clientId ?? l.id)),
              )
              .filter((n: any) => Number.isFinite(n) && n > 0),
          ),
        ]
      : [...new Set((cs.clIds || []).filter((n: any) => Number.isFinite(n)))];
  /* 175 §3.3 — THE PANE'S SILENCE WAS THE OTHER HALF OF THE DEFECT. This
     filtered out any linked client with no stored folder, so a client whose
     provisioning failed and a viewer with no right to see client files looked
     EXACTLY the same: nothing rendered. The two are now different — the right
     still gates the whole pane (`_showClientFiles`, unchanged, fail-closed),
     and a linked client with no path gets a row that SAYS it has no folder.
     `path` is nullable from here down; the row is the discriminator. */
  /* 216 §4 [RULED 71, 73] — a collective case's rows in the one order (by last name, a company by its name); a
     regular matter's keep the order this pane always drew, by the name as shown (ruling 73: not in this PR). */
  const _orderedClientIds = kind === "cc" ? orderIdsByParty(_linkedClientIds, cl as readonly unknown[]) : _linkedClientIds;
  const _keepOrder = () => 0;
  const _byShownName = (a: { name?: string | null }, b: { name?: string | null }) =>
    (a.name ?? "").localeCompare(b.name ?? "", "fr", { sensitivity: "base" });
  const _rowOrder = kind === "cc" ? _keepOrder : _byShownName;
  const _clientFileRows = _showClientFiles
    ? _orderedClientIds
        .map((id) => (cl || []).find((c: any) => c.id === id))
        .filter((c) => c)
        .map((c) => {
          const _path = (c.clientRootFolder as string | null) || null;
          const _name = _clientName(c);
          /* 176 §2.1 — THE FOLDER'S OWN NAME, where the row only ever had the
             CLIENT's. Renaming a client never renames its SharePoint folder
             (§1's ruling), so the two drift apart and the row said nothing
             about it: a reader looking for "ACME NV" on the drive found the
             folder it was created as.

             `folderLastSegment` (lib/folderName.mjs:62) and not a second path
             splitter — the same function `folderRefIsStale` asks the question
             through, so the two ends cannot disagree about what a folder's
             name IS.

             Derived HERE and not in the JSX, and it carries its own render
             condition: null when the folder's name agrees with the client's,
             which is the usual case, because a name printed twice is noise. */
          const _seg = folderLastSegment(_path) as string | null;
          return {
            id: c.id,
            name: _name,
            path: _path,
            folderName: _seg && _seg !== _name ? _seg : null,
          };
        })
        .sort(_rowOrder)
    : [];

  return (
    <>
      <SectionHeader
        title={t(titleKey, { fallback: "Documents" })}
        collapsible={collapsible}
        open={sectionOpen}
        onToggle={onSectionToggle}
      />
      {(!collapsible || sectionOpen) && (
        <>
          {/* ═════ Sub-pane (i) Case file ═════ */}
          <div className="detail-card" style={{ marginBottom: 10 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                marginBottom: 8,
              }}
            >
              <h4 style={PANE_TITLE}>
                {t("docs.pane.case_file", { fallback: "Case file" })}
              </h4>
              {/* 176 §1.2 — THE FOLDER'S NAME IS PERMANENT [RULED, Laurent 2026-09-05].
                  Renaming a matter or a client in the app never renames its
                  SharePoint folder: the link points at the folder, not at its
                  name, so a folder may keep an old — or misspelled — name for
                  ever and that is CORRECT. The one thing in this app that
                  renames a folder is the stale-prospect-ref reconcile below
                  (:795), and it does so only when clicked.

                  `!docLocked` — the tip is guidance for someone about to
                  change something, so it appears with the affordances and not
                  beside a read-only pane. */}
              {!docLocked && (
                <InfoHint
                  title={t("folder.permanence_tip", {
                    fallback:
                      "The SharePoint folder keeps the name it was created with. Renaming a matter or a client here never renames the folder — the link points at the folder itself, so it keeps working whatever the folder is called.",
                  })}
                />
              )}
              <div
                style={{
                  marginLeft: "auto",
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                }}
              >
                <AddButton
                  label={t("docs.add_link", { fallback: "Link" })}
                  onClick={() => setLinkMod({ type: "other" })}
                  visible={!docLocked && (!!isAdmin || isRPHere)}
                />
                {/* Phase 25.1 B.3: lock relocated here from the Documents
                    section header — the Case file pane is the only pane it
                    governs. */}
                <LockButton
                  locked={docLocked}
                  onToggle={() => setDocLocked((v: any) => !v)}
                />
              </div>
            </div>
            <div style={PANE_BODY}>
              {/* Folder card — agenda-event row shell (.ai). */}
              {canViewDocs && !fn && (
                <div className="event-item" style={{ alignItems: "center" }}>
                  <Folder
                    size={16}
                    style={{ color: "var(--tm)", flexShrink: 0 }}
                  />
                  <span
                    className="event-content"
                    style={{
                      fontSize: FS.s11_5,
                      color: "var(--tm)",
                      fontStyle: "italic",
                    }}
                  >
                    {t("modal.case.no_folder_yet", {
                      fallback: "No folder linked yet",
                    })}
                  </span>
                  {canLinkDocs ? (
                    <>
                      <button
                        type="button"
                        className="btn btn-ghost"
                        style={{
                          padding: "3px 8px",
                          fontSize: FS.s10_5,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                        title={t("modal.case.create_case_folder", {
                          fallback: "Create Case Folder",
                        })}
                        onClick={_create}
                      >
                        <Folder size={11} />
                        {t("modal.case.create_case_folder", {
                          fallback: "Create Case Folder",
                        })}
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost"
                        style={{
                          padding: "3px 8px",
                          fontSize: FS.s10_5,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                        title={t("modal.case.link_existing_folder", {
                          fallback: "Link existing folder",
                        })}
                        onClick={_linkOrRelink}
                      >
                        <Link2 size={11} />
                        {t("modal.case.link_existing_folder", {
                          fallback: "Link existing folder",
                        })}
                      </button>
                    </>
                  ) : (
                    <span
                      title={t("modal.case.no_folder_no_permission", {
                        fallback:
                          "No folder linked. You don't have permission to create or link a folder.",
                      })}
                      style={{ display: "inline-flex" }}
                    >
                      <Lock
                        size={12}
                        style={{ color: "var(--tm)", flexShrink: 0 }}
                      />
                    </span>
                  )}
                </div>
              )}

              {canViewDocs && fn && (
                <div className="event-item" style={{ alignItems: "center" }}>
                  <Folder
                    size={16}
                    style={{ color: "var(--ac)", flexShrink: 0 }}
                  />
                  {/* ── 166 §I.1(a) — THE NAME IS THE CONTROL ────────────────
                      Laurent's ruling 11, 2026-09-04: clicking a folder's name
                      opens it, and the icon-plus-"Ouvrir" button beside it
                      goes. Same handler (`_doOpen`), same permission gate
                      (`canViewDocs && fn`, the condition this whole row is
                      already inside), the button's `title` moved onto the
                      name, and the hover the app already uses for a clickable
                      name — `className="link"` (`App.css:795`: the accent
                      colour, a pointer, an underline on hover), censused
                      rather than invented (AgendaView's matter chip is one of
                      seven sites). `role="button"` and the Enter/Space handler
                      make it reachable without a mouse, which the <button> it
                      replaces was for free. */}
                  <span
                    className="event-content link"
                    role="button"
                    tabIndex={0}
                    title={t("modal.case.open_in_sharepoint", {
                      fallback: "Open folder in SharePoint",
                    })}
                    style={{
                      fontSize: FS.s11_5,
                      fontWeight: 600,
                      color: "var(--ac)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    onClick={() => void _doOpen()}
                    onKeyDown={(ev) => {
                      if (ev.key === "Enter" || ev.key === " ") {
                        ev.preventDefault();
                        void _doOpen();
                      }
                    }}
                  >
                    {fn}
                  </span>
                  {/* 166 §I.2 — THE PANE LINKS AND UNLINKS; IT DOES NOT EDIT.
                      Ruling 12: changing what a case's folder IS belongs to
                      the Case edit modal, and MEASURED before the pencil was
                      removed — it is there: `CaseModal.tsx:1044` carries the
                      same `modal.case.relink_folder` control behind
                      `_canLinkFolder`, and the CC edit modal carries its twin
                      at `CollectiveCaseView.tsx:4311`. So this is not the only
                      path to a relink and removing it strands nobody. "+ Lien"
                      (:600) still attaches a folder when none is linked, and
                      the unlink X below still detaches one (ruling 14 —
                      anything the pane can add, the pane can remove). */}
                  {canLinkDocs && !docLocked && (
                    <>
                      <button
                        type="button"
                        className="btn"
                        style={{
                          padding: "2px 6px",
                          fontSize: FS.s10,
                          display: "inline-flex",
                          alignItems: "center",
                          background: "rgba(248,113,113,.1)",
                          color: "var(--rd)",
                          border: "1px solid rgba(248,113,113,.3)",
                        }}
                        title={t("modal.case.unlink_folder", {
                          fallback: "Unlink folder from this case",
                        })}
                        onClick={_unlink}
                      >
                        <X size={10} />
                      </button>
                    </>
                  )}
                  {!canLinkDocs && (
                    <Lock
                      size={11}
                      style={{
                        color: "var(--tm)",
                        flexShrink: 0,
                        marginLeft: 4,
                      }}
                    />
                  )}
                </div>
              )}

              {/* 140 THE RECONCILE — the third state of this block, beside "no
                  folder linked yet" and the folder row above. Same
                  `caseView.documents.link` gate the manual Create Case Folder
                  button uses (`canLinkDocs`; the route behind it is
                  microsoft.js's create-case-folder, under the same section
                  key). It NAMES THE FACT and offers the change — the folder is
                  not called wrong, because a firm may have renamed it on
                  purpose. Ordinary body colour, no warning tint, no icon that
                  reads as an alarm: this is an offer, clicked. */}
              {canViewDocs && canLinkDocs && _staleFolderRef && _renameTo && (
                <div className="event-item" style={{ alignItems: "center" }}>
                  <Folder
                    size={16}
                    style={{ color: "var(--tm)", flexShrink: 0 }}
                  />
                  <span
                    className="event-content"
                    style={{ fontSize: FS.s11, color: "var(--tm)" }}
                  >
                    {t("case.folder.still_prospect_ref", {
                      ref: _staleFolderRef,
                      fallback: `This folder still carries the prospect reference ${_staleFolderRef}.`,
                    })}{" "}
                    {t("case.folder.rename_to_q", {
                      name: _renameTo,
                      fallback: `Rename it to ${_renameTo}?`,
                    })}
                  </span>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    disabled={reconciling}
                    style={{
                      padding: "3px 8px",
                      fontSize: FS.s10_5,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      flexShrink: 0,
                    }}
                    title={t("case.folder.rename_to_q", {
                      name: _renameTo,
                      fallback: `Rename it to ${_renameTo}?`,
                    })}
                    onClick={_reconcileFolder}
                  >
                    <Pencil size={11} />
                    {t("common.rename", { fallback: "Rename" })}
                  </button>
                </div>
              )}

              {/* Links list — agenda-event row shell (.ai). */}
              {__pg.map((lk2: any) => {
                const LI = getLinkIcon(lk2.type);
                const lc = getLinkColor(lk2.type);
                const nc2 = getLinkTag(lk2.type);
                const canDel = isAdminLink || lk2.addedBy === curIni;
                return (
                  <div
                    key={lk2.id}
                    className="event-item"
                    style={{ alignItems: "center" }}
                  >
                    <LI size={14} style={{ color: lc, flexShrink: 0 }} />
                    <div
                      className="event-content"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 7,
                        flexWrap: "wrap",
                        minWidth: 0,
                      }}
                    >
                      <a
                        href={lk2.url}
                        target="_blank"
                        rel="noopener"
                        style={{
                          color: nc2.bg,
                          textDecoration: "none",
                          fontSize: FS.s11_5,
                          fontWeight: 500,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          minWidth: 0,
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {lk2.name}
                      </a>
                      <span
                        className="tag"
                        style={{ fontSize: FS.s8_5, flexShrink: 0 }}
                      >
                        {nc2.name}
                      </span>
                      <span
                        style={{
                          fontSize: FS.s10,
                          color: "var(--tm)",
                          fontFamily: "'JetBrains Mono',monospace",
                          flexShrink: 0,
                        }}
                      >
                        {fd(lk2.dateAdded)}
                      </span>
                      {lk2.addedBy && (
                        <span
                          style={{
                            fontSize: FS.s8,
                            color: "var(--tm)",
                            background: "var(--bc)",
                            border: "1px solid var(--br)",
                            borderRadius: RAD.r8,
                            padding: "0 4px",
                            flexShrink: 0,
                          }}
                        >
                          {lk2.addedBy}
                        </span>
                      )}
                    </div>
                    {!docLocked && (
                      <button
                        className="btn btn-ghost"
                        style={{ padding: "1px 4px", fontSize: FS.s9 }}
                        onClick={() =>
                          setLinkMod({
                            _id: lk2.id,
                            name: lk2.name,
                            url: lk2.url,
                            type: lk2.type,
                          })
                        }
                      >
                        <Pencil size={11} />
                      </button>
                    )}
                    {!docLocked && canDel && (
                      <button
                        className="btn btn-danger"
                        style={{ padding: "1px 4px", fontSize: FS.s9 }}
                        onClick={() => onDeleteLink(lk2)}
                      >
                        <Trash2 size={11} />
                      </button>
                    )}
                  </div>
                );
              })}

              {paginate && __tot > 1 && <Pager cur={__cur} tot={__tot} setPage={pager.setPage} />}
            </div>
          </div>
          {/* ═════ end Sub-pane (i) Case file ═════ */}

          {/* ═════ Sub-pane — Client files (per linked client) ═════
              Phase 41 (D4). One row per linked client, gated fail-closed
              (admin OR RP-here). Collapsed by default on CCV (kind==='cc'),
              expanded on CaseView — driven by `kind`. 175 §3.3: the row is
              present whether or not the client has a stored folder, and says
              which.

              176 §3.1 — AND THE PANE IS PRESENT WHETHER OR NOT THERE ARE ROWS.
              The condition was `canViewDocs && _clientFileRows.length > 0`, so
              a matter with no linked client rendered NOTHING — which is again
              indistinguishable from having no right to see the pane, the exact
              defect 175 §3.3 closed one layer down. `_showClientFiles` is the
              right, and it is what the condition asks now.

              `_showClientFiles` (:542) is UNCHANGED and stays fail-closed: not
              widened, not moved, and deliberately not made to depend on the
              row count. It is the only thing between a viewer without the
              right and a client's folder, and a pane that renders an empty
              state is not a pane that leaks one. */}
          {canViewDocs && _showClientFiles && (
            <div className="detail-card" style={{ marginBottom: 10 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  cursor: "pointer",
                  marginBottom: clientFilesOpen ? 8 : 0,
                }}
                onClick={() => setClientFilesOpen((v) => !v)}
              >
                {clientFilesOpen ? (
                  <ChevronDown
                    size={13}
                    style={{ color: "var(--tm)", flexShrink: 0 }}
                  />
                ) : (
                  <ChevronRight
                    size={13}
                    style={{ color: "var(--tm)", flexShrink: 0 }}
                  />
                )}
                <h4 style={{ ...PANE_TITLE, margin: 0 }}>
                  {t("docs.pane.client_files", { fallback: "Client files" })}
                </h4>
                {/* 176 §1.2(b) — same sentence, same rule, and DELIBERATELY the
                    same `docLocked`: this pane has no lock of its own and must
                    not acquire one (§1.2), because it sits inside the section
                    that lock already governs. */}
                {!docLocked && (
                  <InfoHint
                    title={t("folder.permanence_tip", {
                      fallback:
                        "The SharePoint folder keeps the name it was created with. Renaming a matter or a client here never renames the folder — the link points at the folder itself, so it keeps working whatever the folder is called.",
                    })}
                  />
                )}
                <span style={{ fontSize: FS.s10, color: "var(--tm)" }}>
                  ({_clientFileRows.length})
                </span>
              </div>
              {clientFilesOpen && (
                <div style={PANE_BODY}>
                  {/* 176 §3.1 — the empty state, in the shell its siblings
                      wear (`className="event-item"`), italic `var(--tm)` like
                      the case pane's own "No folder linked yet" (:645). It
                      says the matter has no client — a fact — and offers
                      nothing, because linking a client to a matter is not this
                      pane's job. */}
                  {_clientFileRows.length === 0 && (
                    <div className="event-item" style={{ alignItems: "center" }}>
                      <Folder
                        size={16}
                        style={{ color: "var(--tm)", flexShrink: 0 }}
                      />
                      <span
                        className="event-content"
                        style={{
                          fontSize: FS.s11_5,
                          color: "var(--tm)",
                          fontStyle: "italic",
                        }}
                      >
                        {t("clientFile.no_client_linked", {
                          fallback: "No client linked to this matter.",
                        })}
                      </span>
                    </div>
                  )}
                  {_clientFileRows.map((r) => (
                    <div
                      key={r.id}
                      className="event-item"
                      style={{ alignItems: "center" }}
                    >
                      <Folder
                        size={16}
                        style={{
                          color: r.path ? "var(--ac)" : "var(--tm)",
                          flexShrink: 0,
                        }}
                      />
                      {/* 166 §I.1(a) — the second site, same rule. These rows
                          carry NO editor to remove: censused, a client-file row
                          has only this one control (no pencil, no unlink), so
                          §I.2 has nothing to do here. */}
                      {r.path ? (
                        <span
                          className="event-content link"
                          role="button"
                          tabIndex={0}
                          title={t("clientFile.open", {
                            fallback: "Open client file",
                          })}
                          style={{
                            fontSize: FS.s11_5,
                            fontWeight: 600,
                            color: "var(--tp)",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          onClick={() => openClientFolder(r.id, r.path as string, t)}
                          onKeyDown={(ev) => {
                            if (ev.key === "Enter" || ev.key === " ") {
                              ev.preventDefault();
                              openClientFolder(r.id, r.path as string, t);
                            }
                          }}
                        >
                          {t("clientFile.label", { fallback: "Client file" })} —{" "}
                          {r.name}
                        </span>
                      ) : (
                        /* 175 §3.3 — NO FOLDER IS NOT NO PERMISSION. Not a
                           button: there is nothing to open, and an affordance
                           that cannot act is the same lie one layer down. The
                           title carries the repair, which already exists in the
                           clients list. */
                        <span
                          className="event-content"
                          title={t("clientFile.no_folder_tip", {
                            fallback:
                              "This client has no stored SharePoint folder. An admin can repair it from the clients list, with Provision missing folders.",
                          })}
                          style={{
                            fontSize: FS.s11_5,
                            fontWeight: 600,
                            color: "var(--tm)",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {r.name} —{" "}
                          {t("clientFile.no_folder", {
                            fallback: "no folder on file",
                          })}
                        </span>
                      )}
                      {/* 176 §3.2 — PARITY WITH THE CASE-FILE PANE. That pane
                          offers Create / Link when it has no folder (:641);
                          this one offered nothing, so the row said what was
                          wrong and left the reader to go and find the cure.

                          It opens THE EXISTING REPAIR — App's
                          `openProvisionModal`, the same one the clients list's
                          own button opens — and builds no second provisioning
                          path. Same key, same words, same title as that button,
                          so the control and the thing it opens say the same
                          sentence.

                          176 §3.3 — SCOPED TO THIS ROW'S CLIENT. `[r.id]` and
                          never nothing: unscoped is the clients-list call, and
                          from inside a matter that listed the firm's other
                          clients with their e-mails and their cases. The modal
                          opens on this one client, already ticked.

                          The gate is the one the row's own tip already names
                          two lines up ("An admin can repair it from the
                          clients list"): `isAdmin`. `!docLocked` because it is
                          an edit, like every other affordance in this section.
                          `onProvisionFolders` is checked too — a host that
                          does not thread it gets the row it had before, never
                          a dead button. */}
                      {!r.path && !docLocked && isAdmin && onProvisionFolders && (
                        <button
                          type="button"
                          className="btn btn-ghost"
                          style={{
                            padding: "3px 8px",
                            fontSize: FS.s10_5,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            flexShrink: 0,
                          }}
                          title={t("app.client.backfill_tip", {
                            fallback:
                              "Create SharePoint folders for clients that have none (spaced to avoid throttling; run in batches)",
                          })}
                          onClick={() => onProvisionFolders([r.id])}
                        >
                          <Folder size={11} />
                          {t("app.client.backfill_label", {
                            fallback: "Provision missing folders",
                          })}
                        </button>
                      )}
                      {/* 176 §2.1 — AND THE FOLDER'S OWN NAME, when it is not
                          the client's. `r.folderName` is null when they agree
                          (the usual case) and null whenever `path` is null,
                          which is why this can sit outside the ternary and
                          still never appear on the no-folder arm: a row with
                          no folder has no folder name to print.

                          The shape is CENSUSED, not chosen: a dimmed
                          parenthesised secondary value is what this file
                          already renders 60 lines up for the pane's count
                          (`FS.s10` / `var(--tm)`), and what the tree renders
                          for a client name beside a case (App.tsx:12280), a
                          year beside a book (LibraryView.tsx:1165) and a ref
                          beside a matter (ScheduleView.tsx:910). No new
                          colour. The pane header's own hover (§1.2(b)) is
                          what explains WHY the two names differ. */}
                      {r.folderName && (
                        <span
                          style={{
                            fontSize: FS.s10,
                            color: "var(--tm)",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          ({r.folderName})
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ═════ Sub-pane (ii) Legal references ═════ */}
          {canManageDocs && (
            <div className="detail-card" style={{ marginBottom: 10 }}>
              <h4 style={PANE_TITLE}>
                {t("docs.pane.legal_references", {
                  fallback: "Legal references",
                })}
              </h4>
              <div style={PANE_BODY}>
                <CaseLibraryCopySection
                  caseId={cs.id}
                  kind={kind}
                  toast={toast}
                />
              </div>
            </div>
          )}

          {/* ═════ Sub-pane (iii) Generate procedural documents ═════ */}
          {canGenerate && (
            <div className="detail-card" style={{ marginBottom: 0 }}>
              <h4 style={PANE_TITLE}>
                {t("docs.pane.generate", {
                  fallback: "Generate procedural documents",
                })}
              </h4>
              <ListLoadContext.Provider value={_tplLists}>
                <ListNotLoaded lists={[PAGE_LISTS.docTemplates]}>
                  <div
                    style={{
                      display: "flex",
                      gap: 6,
                      alignItems: "flex-start",
                      flexWrap: "wrap",
                    }}
                  >
                    <MenuSelect
                      className="field-input"
                      style={{ flex: "1 1 240px", minWidth: 180, fontSize: FS.s11 }}
                      value={tplPick}
                      onChange={(v) => setTplPick(v)}
                      options={[
                        [
                          "",
                          t("docs.choose_template", {
                            fallback: "Choose template…",
                          }),
                        ] as const,
                        /* the option's five JSX children were adjacent text nodes;
                           the label is the run of text they rendered */
                        ...templates.map(
                          (x) =>
                            [
                              String(x.id),
                              `${x.code} — ${x.name} (${
                                x.kind === "enumeration"
                                  ? t("docTpl.kind_enum", { fallback: "Enumeration" })
                                  : t("docTpl.kind_prose", { fallback: "Prose" })
                              })`,
                            ] as const,
                        ),
                      ]}
                    />
                    <GenerateDocButton
                      kind={kind}
                      entity={cs}
                      cl={cl}
                      op={op}
                      stages={cs.stages || []}
                      stageTypesList={stageTypesList}
                      toast={toast}
                      assigned={assigned}
                      presetTplId={tplPick}
                      disabled={!tplPick}
                      style={{ padding: "4px 10px", fontSize: FS.s10_5 }}
                    />
                  </div>
                  {templates.length === 0 && (
                    <p
                      style={{
                        fontSize: FS.s10_5,
                        color: "var(--tm)",
                        margin: "6px 0 0",
                        fontStyle: "italic",
                      }}
                    >
                      {t("docgen.no_templates", {
                        fallback:
                          "No templates with an uploaded file. Add one in Settings → Document Templates.",
                      })}
                    </p>
                  )}
                </ListNotLoaded>
              </ListLoadContext.Provider>
            </div>
          )}

          {/* ═════ Sub-pane (iv) Document billing ═════ */}
          {canBillDocs && (
            <div className="detail-card" style={{ marginBottom: 0, marginTop: 10 }}>
              <h4 style={PANE_TITLE}>
                {t("docBilling.pane_title", { fallback: "Document billing" })}
              </h4>
              <button
                type="button"
                className="btn btn-ghost"
                style={{
                  padding: "4px 10px",
                  fontSize: FS.s10_5,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                }}
                onClick={() => setDocBillOpen(true)}
                title={t("docBilling.open_title", {
                  fallback:
                    "Search the case SharePoint folder and bill time on documents",
                })}
              >
                <FileText size={12} style={{ marginRight: 4, verticalAlign: "-2px" }} />
                {t("docBilling.open_button", { fallback: "Bill documents" })}
              </button>
            </div>
          )}

          {linkMod && (
            <LinkModal
              linkMod={linkMod}
              setLinkMod={setLinkMod}
              saveLink={saveLink}
              linkTypesList={linkTypesList}
              cs={cs}
              kind={kind}
              toast={toast}
              t={t}
            />
          )}

          {/* ═════ Sub-pane (v) Renaming documents — 211 §5 ═════ */}
          {canRenameDocs && (
            <div className="detail-card" style={{ marginBottom: 0, marginTop: 10 }}>
              <h4 style={PANE_TITLE}>
                {tr("docRename.pane_title", { fallback: "Renaming documents" })}
              </h4>
              <button
                type="button"
                className="btn btn-ghost"
                style={{ padding: "4px 10px", fontSize: FS.s10_5, display: "inline-flex", alignItems: "center", gap: 4 }}
                onClick={() => {
                  setDocRenameOpen(true);
                }}
                title={tr("docRename.open_title", { fallback: "Rename the folder's files: the date, the name" })}
              >
                <Pencil size={12} style={{ marginRight: 4, verticalAlign: "-2px" }} />
                {tr("docRename.open_button", { fallback: "Rename documents" })}
              </button>
            </div>
          )}

          {(docBillOpen || docRenameOpen) && (
            <Suspense fallback={null}>
              {/* 211 §5 — one mount, two purposes: the billing window, or its file list renaming */}
              <DocumentBillingModal
                open
                purpose={docRenameOpen ? "rename" : "bill"}
                onClose={() => {
                  setDocBillOpen(false);
                  setDocRenameOpen(false);
                }}
                cs={cs}
                kind={kind}
                toast={toast}
                onBilled={() =>
                  window.dispatchEvent(
                    new CustomEvent("lx:reload-time-entries"),
                  )
                }
              />
            </Suspense>
          )}
        </>
      )}
    </>
  );
}

function LinkModal({
  linkMod,
  setLinkMod,
  saveLink,
  linkTypesList,
  cs,
  kind,
  toast,
  t,
}: any) {
  const detectType = (u: any) => {
    const ul = (u || "").toLowerCase();
    if (ul.includes("sharepoint.com")) return "sharepoint";
    if (ul.includes("onedrive")) return "onedrive";
    if (ul.includes("dropbox.com")) return "dropbox";
    if (ul.includes("drive.google.com")) return "gdrive";
    return null;
  };
  const setUrlDt = (u: any) => {
    const dt = detectType(u);
    setLinkMod((p: any) => ({
      ...p,
      url: u,
      ...(dt && !p._typeManual ? { type: dt } : {}),
    }));
  };
  const LINK_TYPES = (linkTypesList || []).map((lt: any) => [lt.id, lt.name]);

  return (
    <ModalLayout
      onClose={() => setLinkMod(null)}
      onSave={() => saveLink(linkMod)}
      saveLabel={t("common.save", { fallback: "Save" })}
      maxWidth={420}
      headerContent={
        <div className="modal-header">
          <h3>
            {linkMod._id
              ? t("docs.link.modal.edit_title", { fallback: "Edit Link" })
              : t("docs.link.modal.add_title", { fallback: "Add Link" })}
          </h3>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => setLinkMod(null)}
            style={{ padding: "2px 6px" }}
          >
            <X size={11} />
          </button>
        </div>
      }
    >
          <div className="field">
            <label>{t("field.name_required", { fallback: "Name *" })}</label>
            <input autoComplete="off" /* 207 §18 */
              className="field-input no-contact-autofill"
              autoFocus
              value={linkMod.name || ""}
              onChange={(e) =>
                setLinkMod((p: any) => ({ ...p, name: e.target.value }))
              }
              placeholder={t("docs.link.modal.name_ph", {
                fallback: "e.g. SharePoint Case Folder",
              })}
            />
          </div>
          <div className="field">
            <label>
              {t("docs.link.modal.url_required", { fallback: "URL *" })}
            </label>
            <div style={{ display: "flex", gap: 4 }}>
              <input
                className="field-input"
                style={{ flex: 1 }}
                value={linkMod.url || ""}
                onChange={(e) => setUrlDt(e.target.value)}
                placeholder={t("docs.link.modal.url_ph", {
                  fallback: "https://... or file path",
                })}
              />
              <label
                className="btn btn-ghost"
                style={{
                  padding: "3px 8px",
                  fontSize: FS.s10,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 3,
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
              >
                {t("modal.doc.browse", { fallback: "Browse" })}
                <input
                  type="file"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    setLinkMod((p: any) => ({
                      ...p,
                      ...(!p.name
                        ? { name: file.name.replace(/\.[^.]+$/, "") }
                        : {}),
                    }));
                    e.target.value = "";
                  }}
                />
              </label>
              <button
                type="button"
                className="btn btn-ghost"
                style={{
                  padding: "3px 8px",
                  fontSize: FS.s10,
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
                title={t("modal.doc.browse_onedrive", {
                  fallback: "Browse OneDrive",
                })}
                onClick={async () => {
                  try {
                    const picked = await openOneDrivePicker({
                      basePath: cs?.caseRootFolder || null,
                      ...(kind === "cc"
                        ? { ccId: cs?.id || null }
                        : { caseId: cs?.id || null }),
                    });
                    if (!picked) return;
                    const nm = picked.name || "";
                    setLinkMod((p: any) => ({
                      ...p,
                      url: picked.webUrl || p.url,
                      ...(!p.name && nm
                        ? { name: nm.replace(/\.[^.]+$/, "") }
                        : {}),
                      type: p._typeManual ? p.type : "onedrive",
                    }));
                  } catch (e2) {
                    toast(
                      "⚠ " +
                        ((e2 as any).message ||
                          t("toast.onedrive_picker_failed", {
                            fallback: "OneDrive picker failed",
                          })),
                    );
                  }
                }}
              >
                <Folder size={12} style={{ marginRight: 4, verticalAlign: "-2px" }} />
                {t("modal.doc.onedrive", { fallback: "OneDrive" })}
              </button>
              <button
                type="button"
                className="btn btn-ghost"
                style={{
                  padding: "3px 8px",
                  fontSize: FS.s10,
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
                onClick={() => {
                  navigator.clipboard
                    .readText()
                    .then((v) => {
                      if (v) {
                        setUrlDt(v.trim());
                        if (!linkMod.name) {
                          const nm = v
                            .trim()
                            .replace(/\\/g, "/")
                            .split("/")
                            .pop();
                          if (nm)
                            setLinkMod((p: any) => ({
                              ...p,
                              name: p.name || nm.replace(/\.[^.]+$/, ""),
                            }));
                        }
                      }
                    })
                    .catch(() =>
                      toast(
                        t("toast.clipboard_denied", {
                          fallback:
                            "⚠ Clipboard access denied — paste manually",
                        }),
                      ),
                    );
                }}
              >
                {t("modal.doc.paste_path", { fallback: "Paste Path" })}
              </button>
            </div>
            <p
              style={{
                fontSize: FS.s9,
                color: "var(--tm)",
                margin: "3px 0 0",
                lineHeight: 1.4,
              }}
            >
              {t("docs.link.modal.path_help", {
                fallback:
                  "Paste a web URL, or use Browse to get the filename then Paste Path for the full path. In Finder: right-click > Hold Option > Copy as Pathname",
              })}
            </p>
          </div>
          <div className="field">
            <label>{t("common.type", { fallback: "Type" })}</label>
            <MenuSelect
              className="field-input"
              value={linkMod.type || "other"}
              onChange={(v) =>
                setLinkMod((p: any) => ({
                  ...p,
                  type: v,
                  _typeManual: true,
                }))
              }
              options={LINK_TYPES.map(
                ([v, l]: any) => [String(v), String(l ?? "")] as const,
              )}
            />
          </div>
    </ModalLayout>
  );
}
